import React, { useState, useContext, useMemo, useEffect } from 'react';
import { AppContext } from '../../context/AppContext';
import ContractDocumentView from '../../components/ContractDocumentView';
import ContractSignModal from '../../components/ContractSignModal';
import ContractCreateModal from '../../components/ContractCreateModal';
import CompanySealModal from '../../components/CompanySealModal';
import ResignationSubmitModal from '../../components/ResignationSubmitModal';
import ResignationWithdrawModal from '../../components/ResignationWithdrawModal';
import ResignationPolicyModal from '../../components/ResignationPolicyModal';
import { 
  FileText, 
  PenTool, 
  CheckCircle2, 
  Clock, 
  Plus, 
  Share2, 
  Copy, 
  Check, 
  Send, 
  ShieldCheck, 
  Building2, 
  Eye, 
  X,
  Printer,
  Sparkles,
  RotateCcw,
  RotateCw,
  Lock,
  Calendar,
  Stamp,
  SlidersHorizontal,
  Trash2,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';

const MobileContractTab = () => {
  const { 
    contracts, 
    currentUser, 
    deleteContract, 
    requestContractDeletion,
    acceptContractDeletion,
    rejectContractDeletion,
    sendContractReminder, 
    extendContractExpiration,
    signContract,
    companySeal,
    updateCompanySeal,
    fetchRemoteContracts
  } = useContext(AppContext);

  const isAdmin = currentUser?.Role === 'ADMIN' || currentUser?.UserID === 'emp-09' || currentUser?.UserID === 'emp-10' || currentUser?.UserID === 'clou0315';

  const [activeSegment, setActiveSegment] = useState(isAdmin ? 'admin' : 'my'); // 'my' | 'admin'
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSealModalOpen, setIsSealModalOpen] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [isResignationModalOpen, setIsResignationModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Auto-sync remote contracts when active segment changes
  useEffect(() => {
    if (fetchRemoteContracts) {
      fetchRemoteContracts();
    }
  }, [activeSegment]);

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    if (fetchRemoteContracts) {
      await fetchRemoteContracts();
    }
    setTimeout(() => setIsRefreshing(false), 500);
  };
  const [signingContract, setSigningContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  const [withdrawingContract, setWithdrawingContract] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Requirement 1: Deletion Agreement Modals in Mobile
  const [deletionRequestModalContract, setDeletionRequestModalContract] = useState(null);
  const [deletionReasonInput, setDeletionReasonInput] = useState('');
  const [deletionAgreementModalContract, setDeletionAgreementModalContract] = useState(null);

  const handleDeleteClick = (c) => {
    const isSigned = c.Status === 'SIGNED' || c.Status === 'APPROVED' || Boolean(c.EmployeeSignedAt);

    if (!isSigned) {
      if (confirm(`'${c.Title}' 미체결 계약서를 삭제하시겠습니까?`)) {
        deleteContract(c.ContractID);
      }
      return;
    }

    if (c.Status === 'DELETE_REQUESTED') {
      const myId = currentUser?.UserID || 'ADMIN';
      const myEmail = (currentUser?.Email || '').toLowerCase();
      const isMyRequest = (c.DeleteRequestedBy === myId) || (c.DeleteRequestedBy && c.DeleteRequestedBy.toLowerCase() === myEmail);

      if (isMyRequest) {
        alert(`[삭제 합의 수락 대기 중]\n상대방의 삭제 합의 수락을 기다리는 중입니다.`);
      } else {
        setDeletionAgreementModalContract(c);
      }
    } else {
      setDeletionRequestModalContract(c);
      setDeletionReasonInput('');
    }
  };

  // Search, Status, Period & Pagination states for Mobile Admin
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'SIGNED' | 'FREELANCER' | 'RESIGNATION'
  const [periodFilter, setPeriodFilter] = useState('ALL'); // 'ALL' | 'TODAY' | 'MONTH' | 'YEAR' | 'CUSTOM'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // My contracts
  const myContracts = useMemo(() => {
    if (!currentUser) return [];
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myId = currentUser.UserID;
    const myName = currentUser.Name;

    return contracts.filter(c => {
      if (c.UserID && c.UserID === myId) return true;
      if (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) return true;
      if (c.UserName === myName) return true;
      return false;
    });
  }, [contracts, currentUser]);

  const activeResignation = useMemo(() => {
    return myContracts.find(c => c.IsResignation && c.Status !== 'APPROVED' && c.Status !== 'REJECTED' && c.Status !== 'WITHDRAWN');
  }, [myContracts]);

  const myPending = useMemo(() => myContracts.filter(c => c.Status === 'PENDING' && !c.IsResignation), [myContracts]);
  const mySigned = useMemo(() => myContracts.filter(c => c.Status === 'SIGNED' || c.Status === 'APPROVED' || c.Status === 'DELETE_REQUESTED' || c.IsResignation), [myContracts]);

  // Admin stats
  const totalCount = contracts.length;
  const signedCount = contracts.filter(c => c.Status === 'SIGNED' || c.Status === 'APPROVED' || c.Status === 'DELETE_REQUESTED').length;
  const pendingCount = contracts.filter(c => c.Status === 'PENDING').length;
  const freelanceCount = contracts.filter(c => c.TargetType === 'FREELANCER').length;
  const resignationCount = contracts.filter(c => Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA' || c.ContractType === 'HANDOVER')).length;

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, periodFilter, customStartDate, customEndDate]);

  // Target Contracts Filtered (Enforces strict self-ownership isolation for non-admins)
  const targetContractList = useMemo(() => {
    if (!isAdmin || activeSegment === 'my') {
      return myContracts;
    }
    return contracts;
  }, [isAdmin, activeSegment, myContracts, contracts]);

  const filteredContracts = useMemo(() => {
    return targetContractList.filter(c => {
      const matchSearch = !searchTerm || 
        (c.Title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.UserName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.Department || '').toLowerCase().includes(searchTerm.toLowerCase());

      let matchStatus = true;
      if (statusFilter === 'PENDING') matchStatus = c.Status === 'PENDING';
      else if (statusFilter === 'SIGNED') matchStatus = c.Status === 'SIGNED' || c.Status === 'APPROVED' || c.Status === 'DELETE_REQUESTED';
      else if (statusFilter === 'FREELANCER') matchStatus = c.TargetType === 'FREELANCER';
      else if (statusFilter === 'RESIGNATION') matchStatus = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA' || c.ContractType === 'HANDOVER');

      let matchPeriod = true;
      const targetDateStr = c.CreatedAt ? c.CreatedAt.slice(0, 10) : (c.ContractStartDate || '');
      if (targetDateStr && periodFilter !== 'ALL') {
        const today = new Date().toISOString().slice(0, 10);
        const thisMonth = today.slice(0, 7);
        const thisYear = today.slice(0, 4);

        if (periodFilter === 'TODAY') {
          matchPeriod = targetDateStr === today;
        } else if (periodFilter === 'MONTH') {
          matchPeriod = targetDateStr.startsWith(thisMonth);
        } else if (periodFilter === 'YEAR') {
          matchPeriod = targetDateStr.startsWith(thisYear);
        } else if (periodFilter === 'CUSTOM') {
          if (customStartDate && targetDateStr < customStartDate) matchPeriod = false;
          if (customEndDate && targetDateStr > customEndDate) matchPeriod = false;
        }
      }

      return matchSearch && matchStatus && matchPeriod;
    });
  }, [targetContractList, searchTerm, statusFilter, periodFilter, customStartDate, customEndDate]);

  const totalPages = Math.max(1, Math.ceil(filteredContracts.length / itemsPerPage));
  const pagedContracts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredContracts.slice(start, start + itemsPerPage);
  }, [filteredContracts, currentPage, itemsPerPage]);

  // Copy KakaoTalk Link
  const handleCopyLink = (contract) => {
    const url = `${window.location.origin}/#/contract/sign?token=${contract.PublicToken}`;
    navigator.clipboard.writeText(url);
    setCopiedId(contract.ContractID);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="min-h-[calc(100vh-120px)] bg-slate-50 text-slate-800 pb-20 text-left">
      {/* Top Mobile Bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-30 shadow-2xs">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100 shrink-0">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-900">전자계약</h1>
              <p className="text-[10px] text-slate-500">법적 효력 전자서명 & 영구 보관</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="p-1.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-bold border border-slate-200 active:scale-95"
              title="실시간 동기화"
            >
              <RotateCw className={`w-4 h-4 text-indigo-600 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>

            {isAdmin && (
              <>
                <button
                  onClick={() => setIsPolicyModalOpen(true)}
                  className="p-1.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-bold border border-slate-200 active:scale-98"
                  title="사직원 양식 설정"
                >
                  <SlidersHorizontal className="w-4 h-4 text-indigo-600" />
                </button>
                <button
                  onClick={() => setIsSealModalOpen(true)}
                  className="p-1.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-bold border border-slate-200 active:scale-98"
                  title="회사 직인 설정"
                >
                  <Stamp className="w-4 h-4 text-rose-600" />
                </button>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-bold shadow-xs active:scale-98"
                >
                  <Plus className="w-3.5 h-3.5" />
                  새 계약서
                </button>
              </>
            )}
          </div>
        </div>

        {/* Segmented Switch for Admin */}
        {isAdmin && (
          <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1 rounded-xl mt-3 text-xs font-bold">
            <button
              onClick={() => setActiveSegment('admin')}
              className={`py-1.5 rounded-lg transition-all ${
                activeSegment === 'admin' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-500'
              }`}
            >
              전사 계약 현황 ({contracts.length})
            </button>
            <button
              onClick={() => setActiveSegment('my')}
              className={`py-1.5 rounded-lg transition-all flex items-center justify-center gap-1 ${
                activeSegment === 'my' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-500'
              }`}
            >
              내 계약서 ({myContracts.length})
              {myPending.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>}
            </button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        
        {/* ========================================================================= */}
        {/* MY CONTRACTS                                                              */}
        {/* ========================================================================= */}
        {activeSegment === 'my' && (
          <div className="space-y-4">

            {/* Mobile Resignation Single Action Card */}
            {activeResignation ? (
              <div className="bg-gradient-to-r from-slate-900 to-indigo-950 rounded-2xl p-4 text-white shadow-md space-y-3 border border-amber-500/40">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center text-white shrink-0 animate-pulse">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-xs font-bold">퇴사 결재 진행 중 ({activeResignation.CurrentStep || 1}단계)</h3>
                      <span className="text-[9px] bg-amber-500/30 text-amber-200 border border-amber-400/40 px-1.5 py-0.5 rounded-full font-bold">
                        {activeResignation.CurrentStep === 1 ? '인수인계 확인' :
                         activeResignation.CurrentStep === 2 ? '팀장 승인' :
                         activeResignation.CurrentStep === 3 ? '2차 권한자' : '대표이사 결재'}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-300 mt-0.5">
                      인수인계자: {activeResignation.HandoverRecipientName || activeResignation.HandoverRecipient || '지정 담당자'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    disabled={true}
                    className="py-2 bg-slate-800 text-slate-400 rounded-xl text-xs font-bold shadow-xs flex items-center justify-center gap-1 opacity-70 cursor-not-allowed"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    신청 잠김
                  </button>
                  <button
                    onClick={() => setWithdrawingContract(activeResignation)}
                    className="py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold shadow-xs flex items-center justify-center gap-1 active:scale-98 cursor-pointer"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    퇴사 취하 신청
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-slate-900 to-indigo-950 rounded-2xl p-4 text-white shadow-md space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-600 flex items-center justify-center text-white shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-xs font-bold">임직원 퇴사 신청</h3>
                      <span className="text-[9px] bg-rose-500/30 text-rose-200 border border-rose-400/40 px-1.5 py-0.5 rounded-full font-bold">HR 전자양식</span>
                    </div>
                    <p className="text-[10px] text-slate-300 mt-0.5">사직원, 보안서약서 및 인수인계서 모바일 전자서명</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsResignationModalOpen(true)}
                  className="w-full py-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold shadow-xs flex items-center justify-center gap-1.5 active:scale-98 cursor-pointer"
                >
                  <PenTool className="w-3.5 h-3.5" />
                  사직원(퇴사) 작성 및 제출
                </button>
              </div>
            )}
            
            {/* Pending alert */}
            {myPending.length > 0 && (
              <div className="space-y-2">
                <span className="text-[11px] font-bold text-amber-800 flex items-center gap-1">
                  <PenTool className="w-3.5 h-3.5 text-amber-600" />
                  서명 대기 중인 계약서 ({myPending.length})
                </span>
                {myPending.map(c => (
                  <div key={c.ContractID} className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 shadow-sm space-y-3">
                    <div>
                      <span className="text-[10px] font-bold text-amber-700 bg-white px-2 py-0.5 rounded border border-amber-200 uppercase">
                        {c.ContractType}
                      </span>
                      <h3 className="text-xs font-bold text-slate-900 mt-1.5">{c.Title}</h3>
                      <p className="text-[10px] text-slate-500 mt-0.5">발행일: {c.CreatedAt?.slice(0, 10)}</p>
                    </div>

                    <button
                      onClick={() => setSigningContract(c)}
                      className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-xs flex items-center justify-center gap-1.5 active:scale-98"
                    >
                      <PenTool className="w-3.5 h-3.5" />
                      터치로 지금 서명하기
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Signed Archive */}
            <div className="space-y-2">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">
                내 계약서 보관함 (상시 열람 가능)
              </span>

              {mySigned.length === 0 && myPending.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs bg-white rounded-2xl border border-slate-200 p-6">
                  등록된 계약서 내역이 없습니다.
                </div>
              ) : (
                mySigned.map(c => (
                  <div key={c.ContractID} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-2.5">
                    <div className="flex items-center justify-between">
                      {c.Status === 'DELETE_REQUESTED' ? (
                        <span className="text-[10px] font-black text-rose-800 bg-rose-100 px-2 py-0.5 rounded border border-rose-300 flex items-center gap-1 animate-pulse">
                          <AlertCircle className="w-3 h-3 text-rose-600" />
                          파기 합의 요청
                        </span>
                      ) : c.Status === 'WITHDRAWN' ? (
                        <span className="text-[10px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-300 flex items-center gap-1">
                          <RotateCcw className="w-3 h-3 text-slate-500" />
                          취하 완료
                        </span>
                      ) : c.Status === 'REJECTED' ? (
                        <span className="text-[10px] font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200 flex items-center gap-1">
                          <X className="w-3 h-3 text-rose-500" />
                          결재 반려
                        </span>
                      ) : (c.Status === 'SIGNED' || c.Status === 'APPROVED') ? (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          {c.IsResignation ? '최종 승인 완료' : '체결 완료'}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200 flex items-center gap-1">
                          <Clock className="w-3 h-3 text-indigo-600" />
                          {c.IsResignation ? `${c.CurrentStep || 1}단계 결재 중` : '서명 대기'}
                        </span>
                      )}
                      <span className="text-[10px] text-slate-400 font-mono">
                        {c.EmployeeSignedAt?.slice(0, 10) || c.CreatedAt?.slice(0, 10)}
                      </span>
                    </div>

                    <h3 className="text-xs font-bold text-slate-900">{c.Title}</h3>
                    <p className="text-[11px] text-slate-500">
                      계약기간: {c.ContractStartDate} ~ {c.ContractEndDate || '정규직'}
                    </p>

                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                      <button
                        onClick={() => setViewingContract(c)}
                        className="text-xs font-bold text-indigo-600 flex items-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        전문 열람
                      </button>

                      {c.Status === 'DELETE_REQUESTED' ? (
                        <button
                          onClick={() => handleDeleteClick(c)}
                          className="px-3 py-1 bg-rose-600 text-white rounded-lg text-xs font-bold shadow-xs animate-pulse flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>파기 동의/거절</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDeleteClick(c)}
                          className="px-2.5 py-1 bg-rose-50 text-rose-700 rounded-lg text-xs font-bold flex items-center gap-1"
                          title="계약서 파기 요청"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* ADMIN ALL CONTRACTS                                                       */}
        {/* ========================================================================= */}
        {activeSegment === 'admin' && (
          <div className="space-y-3">
            
            {/* Interactive KPI Quick Chips */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setStatusFilter('ALL')}
                className={`p-2.5 rounded-xl border text-left transition-all ${
                  statusFilter === 'ALL'
                    ? 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500'
                    : 'bg-white border-slate-200'
                }`}
              >
                <div className="text-[10px] font-bold text-slate-500">전사 총 계약</div>
                <div className="text-base font-black text-slate-900 mt-0.5">{totalCount}건</div>
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter('SIGNED')}
                className={`p-2.5 rounded-xl border text-left transition-all ${
                  statusFilter === 'SIGNED'
                    ? 'bg-emerald-50 border-emerald-500 ring-1 ring-emerald-500'
                    : 'bg-white border-slate-200'
                }`}
              >
                <div className="text-[10px] font-bold text-emerald-700">체결 완료</div>
                <div className="text-base font-black text-emerald-700 mt-0.5">{signedCount}건</div>
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter('PENDING')}
                className={`p-2.5 rounded-xl border text-left transition-all ${
                  statusFilter === 'PENDING'
                    ? 'bg-amber-50 border-amber-500 ring-1 ring-amber-500'
                    : 'bg-white border-slate-200'
                }`}
              >
                <div className="text-[10px] font-bold text-amber-700">서명 대기</div>
                <div className="text-base font-black text-amber-700 mt-0.5">{pendingCount}명</div>
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter('RESIGNATION')}
                className={`p-2.5 rounded-xl border text-left transition-all ${
                  statusFilter === 'RESIGNATION'
                    ? 'bg-rose-50 border-rose-500 ring-1 ring-rose-500'
                    : 'bg-white border-slate-200'
                }`}
              >
                <div className="text-[10px] font-bold text-rose-700">사직원 결재</div>
                <div className="text-base font-black text-rose-700 mt-0.5">{resignationCount}건</div>
              </button>
            </div>

            {/* Search & Period Filter */}
            <div className="bg-white p-3 rounded-2xl border border-slate-200 space-y-2">
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="계약서명, 성명, 부서 검색..."
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-1 focus:ring-indigo-500 outline-none"
              />

              <div className="flex items-center gap-1 overflow-x-auto pt-1 pb-0.5">
                {[
                  { id: 'ALL', label: '전체' },
                  { id: 'TODAY', label: '오늘' },
                  { id: 'MONTH', label: '이번달' },
                  { id: 'YEAR', label: '올해' },
                  { id: 'CUSTOM', label: '직접' }
                ].map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPeriodFilter(p.id)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap transition-all ${
                      periodFilter === p.id
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {periodFilter === 'CUSTOM' && (
                <div className="flex items-center gap-1 pt-1">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={e => setCustomStartDate(e.target.value)}
                    className="flex-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-[10px]"
                  />
                  <span className="text-slate-400 text-xs">~</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={e => setCustomEndDate(e.target.value)}
                    className="flex-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-[10px]"
                  />
                </div>
              )}
            </div>

            {/* List */}
            {pagedContracts.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-2xl border border-slate-200 text-slate-400 text-xs p-4">
                조회된 전자계약 내역이 없습니다.
              </div>
            ) : (
              pagedContracts.map(c => {
                const isFree = c.TargetType === 'FREELANCER';
                const isResig = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA' || c.ContractType === 'HANDOVER');
                const isSigned = c.Status === 'SIGNED' || c.Status === 'APPROVED';

                return (
                  <div key={c.ContractID} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                        isResig ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                        isFree ? 'bg-cyan-50 text-cyan-700 border border-cyan-200' : 
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {isResig ? '사직원(퇴사)' : isFree ? '외부 프리랜서' : '사내 임직원'}
                      </span>

                      {(() => {
                        const isExpired = c.TokenExpiresAt && !isSigned && c.Status === 'PENDING' && new Date(c.TokenExpiresAt) < new Date();
                        if (c.Status === 'DELETE_REQUESTED') {
                          return (
                            <span className="text-[10px] font-black text-rose-800 bg-rose-100 px-2 py-0.5 rounded flex items-center gap-1 border border-rose-300 animate-pulse">
                              <AlertCircle className="w-3 h-3" />
                              삭제 합의 대기
                            </span>
                          );
                        }
                        if (isExpired) {
                          return (
                            <span className="text-[10px] font-black text-rose-700 bg-rose-50 px-2 py-0.5 rounded flex items-center gap-1 border border-rose-200">
                              <Clock className="w-3 h-3 text-rose-600" />
                              서명 기한 만료
                            </span>
                          );
                        }
                        if (c.Status === 'WITHDRAWN') {
                          return (
                            <span className="text-[10px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded flex items-center gap-1 border border-slate-300">
                              <RotateCcw className="w-3 h-3 text-slate-500" />
                              퇴사 취하
                            </span>
                          );
                        }
                        if (c.Status === 'REJECTED') {
                          return (
                            <span className="text-[10px] font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded flex items-center gap-1 border border-rose-200">
                              <X className="w-3 h-3" />
                              결재 반려
                            </span>
                          );
                        }
                        if (isSigned) {
                          return (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1 border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3" />
                              {isResig ? '최종 승인' : '체결 완료'}
                            </span>
                          );
                        }
                        return (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded flex items-center gap-1 border border-amber-200">
                            <Clock className="w-3 h-3" />
                            {isResig ? `${c.CurrentStep || 1}단계 결재 대기` : '서명 대기'}
                          </span>
                        );
                      })()}
                    </div>

                    <div>
                      <h3 className="text-xs font-bold text-slate-900">{c.Title}</h3>
                      <div className="text-[11px] text-indigo-700 font-bold mt-0.5">
                        대상: {c.UserName} ({c.Department})
                      </div>
                      {!isResig && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          금액: {isAdmin ? `₩ ${(c.TotalAmount || c.AnnualSalary || 0).toLocaleString()} 원` : '🔒 보안 정보 (전문열람 참조)'}
                        </div>
                      )}
                    </div>

                    {/* 4-Step Stepper for Mobile */}
                    {isResig && c.ApprovalLine && c.ApprovalLine.length > 0 && (
                      <div className="bg-slate-50 p-2 rounded-xl border border-slate-200/80 space-y-1">
                        <div className="flex items-center justify-between text-[9px] font-bold text-slate-500">
                          <span>4단계 승인선</span>
                          <span className="text-indigo-600">
                            {c.Status === 'APPROVED' ? '완료' : c.Status === 'WITHDRAWN' ? '취하됨' : `${c.CurrentStep || 1}/4 단계`}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-1">
                          {c.ApprovalLine.map(step => {
                            const isDone = step.Status === 'APPROVED';
                            const isCurrent = step.Step === c.CurrentStep && c.Status !== 'APPROVED' && c.Status !== 'WITHDRAWN' && c.Status !== 'REJECTED';
                            return (
                              <div
                                key={step.Step}
                                className={`p-1 rounded text-center text-[8px] font-bold truncate ${
                                  isDone ? 'bg-emerald-100 text-emerald-800' :
                                  isCurrent ? 'bg-indigo-600 text-white animate-pulse' :
                                  'bg-slate-200/70 text-slate-500'
                                }`}
                              >
                                {step.Step}.{step.Role === 'HANDOVER_ASSIGNEE' ? '인수' : step.Role === 'TEAM_LEADER' ? '팀장' : step.Role === 'HR_MANAGER' ? '인사' : '대표'}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                      <button
                        onClick={() => setViewingContract(c)}
                        className="flex-1 py-1.5 bg-slate-100 text-slate-700 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        전문 열람
                      </button>

                      <button
                        onClick={() => handleCopyLink(c)}
                        className="flex-1 py-1.5 bg-indigo-50 text-indigo-700 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1"
                      >
                        {copiedId === c.ContractID ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5" />}
                        카톡 링크
                      </button>

                      {!isSigned && (
                        <button
                          onClick={async () => {
                            const newDate = await extendContractExpiration(c.ContractID, 7);
                            alert(`서명 링크 유효기간이 7일 연장되었습니다.\n(신규 만료일: ${new Date(newDate).toLocaleDateString()})`);
                          }}
                          className="py-1.5 px-3 bg-sky-50 text-sky-700 rounded-xl text-[11px] font-bold flex items-center justify-center"
                          title="유효기간 7일 재연장"
                        >
                          <RotateCw className="w-3.5 h-3.5" />
                        </button>
                      )}

                      {!isSigned && c.UserEmail && (
                        <button
                          onClick={async () => {
                            const ok = await sendContractReminder(c.ContractID);
                            alert(ok ? '재촉 메일이 발송되었습니다.' : '발송 실패');
                          }}
                          className="py-1.5 px-3 bg-amber-50 text-amber-700 rounded-xl text-[11px] font-bold flex items-center justify-center"
                          title="서명 재촉 메일"
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      )}

                      <button
                        onClick={() => handleDeleteClick(c)}
                        className={`py-1.5 px-3 rounded-xl text-[11px] font-bold flex items-center justify-center ${
                          c.Status === 'DELETE_REQUESTED'
                            ? 'bg-rose-600 text-white animate-pulse'
                            : 'bg-rose-50 text-rose-700'
                        }`}
                        title="계약서 삭제/파기 합의 요청"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white p-2.5 rounded-xl border border-slate-200 text-xs">
                <span className="text-[11px] text-slate-500 font-bold">
                  {currentPage} / {totalPages} 페이지 (총 {filteredContracts.length}건)
                </span>
                <div className="flex items-center gap-1">
                  <button
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    className="px-2 py-1 bg-slate-100 rounded text-xs font-bold disabled:opacity-40"
                  >
                    이전
                  </button>
                  <button
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    className="px-2 py-1 bg-slate-100 rounded text-xs font-bold disabled:opacity-40"
                  >
                    다음
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Contract Sign Modal */}
      {signingContract && (
        <ContractSignModal
          contract={signingContract}
          isOpen={true}
          onClose={() => setSigningContract(null)}
          onSignSuccess={async (cId, sigData, audit) => {
            await signContract(cId, sigData, audit);
            setSigningContract(null);
          }}
        />
      )}

      {/* Company Seal Modal */}
      {isSealModalOpen && (
        <CompanySealModal
          isOpen={true}
          onClose={() => setIsSealModalOpen(false)}
        />
      )}

      {/* Resignation Policy Modal */}
      {isPolicyModalOpen && (
        <ResignationPolicyModal
          isOpen={true}
          onClose={() => setIsPolicyModalOpen(false)}
        />
      )}

      {/* Contract Create Modal */}
      {isCreateModalOpen && (
        <ContractCreateModal
          isOpen={true}
          onClose={() => setIsCreateModalOpen(false)}
          onCreatedSuccess={() => {}}
        />
      )}

      {/* Resignation Submit Modal */}
      {isResignationModalOpen && (
        <ResignationSubmitModal
          isOpen={true}
          onClose={() => setIsResignationModalOpen(false)}
          onSubmittedSuccess={() => {}}
        />
      )}

      {/* Contract View Only Modal */}
      {viewingContract && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-2 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
              <h2 className="text-xs font-bold truncate max-w-[200px]">{viewingContract.Title}</h2>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewingContract(null);
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer relative z-50"
                title="닫기"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 bg-slate-100">
              <ContractDocumentView 
                contract={viewingContract} 
                onOpenWithdrawModal={(c) => setWithdrawingContract(c)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Requirement 1: Mobile Request Deletion Modal */}
      {deletionRequestModalContract && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl border border-slate-200 text-left space-y-3">
            <div className="flex items-center gap-2 text-rose-600 border-b border-rose-100 pb-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <h3 className="font-extrabold text-sm text-slate-900">계약서 파기/삭제 합의 요청</h3>
            </div>

            <p className="text-xs text-slate-600">
              체결된 <strong>'{deletionRequestModalContract.Title}'</strong> 계약서는 상대방의 합의가 필요합니다.
            </p>

            <textarea
              rows={2}
              placeholder="삭제/파기 사유 (선택)..."
              value={deletionReasonInput}
              onChange={(e) => setDeletionReasonInput(e.target.value)}
              className="w-full p-2 bg-slate-50 border border-slate-300 rounded-xl text-xs"
            />

            <div className="flex justify-end gap-1.5 pt-1">
              <button
                type="button"
                onClick={() => setDeletionRequestModalContract(null)}
                className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg"
              >
                취소
              </button>
              <button
                type="button"
                onClick={async () => {
                  await requestContractDeletion(deletionRequestModalContract.ContractID, deletionReasonInput);
                  alert('삭제 합의 요청이 상대방에게 전달되었습니다.');
                  setDeletionRequestModalContract(null);
                }}
                className="px-3 py-1.5 bg-rose-600 text-white text-xs font-bold rounded-lg shadow-2xs"
              >
                합의 요청 발송
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requirement 1: Mobile Deletion Agreement Response Modal */}
      {deletionAgreementModalContract && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl border border-slate-200 text-left space-y-3">
            <div className="flex items-center gap-2 text-amber-600 border-b border-amber-100 pb-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <h3 className="font-extrabold text-sm text-slate-900">상대방의 계약서 파기 합의 요청</h3>
            </div>

            <p className="text-xs text-slate-700 leading-relaxed">
              상대방이 <strong>'{deletionAgreementModalContract.Title}'</strong> 계약서의 파기 합의를 요청했습니다.<br />
              {deletionAgreementModalContract.DeleteRequestedReason && (
                <span className="text-[11px] text-slate-500 block mt-1">사유: {deletionAgreementModalContract.DeleteRequestedReason}</span>
              )}
            </p>

            <div className="flex justify-end gap-1.5 pt-1">
              <button
                type="button"
                onClick={async () => {
                  if (confirm('삭제 합의를 거절하시겠습니까?')) {
                    await rejectContractDeletion(deletionAgreementModalContract.ContractID);
                    alert('삭제 합의 요청을 거절했습니다.');
                    setDeletionAgreementModalContract(null);
                  }
                }}
                className="px-3 py-1.5 bg-slate-200 text-slate-800 text-xs font-bold rounded-lg"
              >
                거절 (계약 유지)
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (confirm('계약서 삭제/파기에 합의하시겠습니까?')) {
                    await acceptContractDeletion(deletionAgreementModalContract.ContractID);
                    alert('계약서가 영구 파기되었습니다.');
                    setDeletionAgreementModalContract(null);
                  }
                }}
                className="px-3 py-1.5 bg-rose-600 text-white text-xs font-bold rounded-lg shadow-2xs"
              >
                합의 수락 및 파기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resignation Withdrawal Modal */}
      {withdrawingContract && (
        <ResignationWithdrawModal
          contract={withdrawingContract}
          isOpen={Boolean(withdrawingContract)}
          onClose={() => setWithdrawingContract(null)}
          onWithdrawnSuccess={() => {
            setWithdrawingContract(null);
            if (viewingContract) setViewingContract(null);
          }}
        />
      )}

      {/* Company Seal Settings Modal */}
      <CompanySealModal
        isOpen={isSealModalOpen}
        onClose={() => setIsSealModalOpen(false)}
        currentSeal={companySeal}
        onSave={updateCompanySeal}
      />
    </div>
  );
};

export default MobileContractTab;
